import { errorInput } from 'src/components/helpers';
import {
  isValidEmail,
  isValidIdentification,
  isValidPhone,
} from 'src/components/helpers/guests/guestFieldValidation';
import { GUEST_IDENTITY_FIELDS } from 'src/components/helpers/guests/guestIdentity';
import Swal from 'sweetalert2';

export const PERSON_FIELDS = GUEST_IDENTITY_FIELDS.filter((field) =>
  ['name', 'lastName', 'identification', 'phone', 'email'].includes(field)
);
export const MAX_CATALOG_IMAGES = 5;

export const yesNoValue = (value) => {
  if (value === true || value === 'Si') return 'Si';
  if (value === false || value === 'No') return 'No';
  return null;
};

export const withPendingRevision = (record = {}) => {
  const { pendingRevision, ...live } = record;
  if (!pendingRevision) return live;
  const { submittedAt, ...proposal } = pendingRevision;
  return { ...live, ...proposal };
};

export const mergeEditingInputs = (record = {}) => {
  const guest = record.Guest || {};
  const merged = { ...record, ...guest, id: record.id, Guest: guest };

  if (guest.id != null) merged.guest_id = guest.id;

  PERSON_FIELDS.forEach((field) => {
    const networkEmpty = record[field] == null || record[field] === '';
    if (networkEmpty && guest[field] != null && guest[field] !== '') {
      merged[field] = guest[field];
    }
    if (merged[field] != null && merged[field] !== '') {
      merged[field] = String(merged[field]);
    }
  });

  const member = yesNoValue(merged.comunidadMemeber);
  if (member) merged.comunidadMemeber = member;

  merged.catalogImages = catalogImagesFromRecord(record.catalogImages);
  merged.jobPostings = jobPostingsFromRecord(record.jobPostings);
  if (record.logo) merged.logo = record.logo;

  return merged;
};

export const applyGuestToInputs = (guest = {}, prev = {}) => {
  const next = {
    ...prev,
    ...guest,
    guest_id: guest.guest_id || guest.id || prev.guest_id,
    identification: guest.identification ?? prev.identification,
    phone: guest.phone ?? prev.phone,
  };

  PERSON_FIELDS.forEach((field) => {
    if (next[field] != null && next[field] !== '') next[field] = String(next[field]);
  });

  return next;
};

export const MAX_JOB_POSTINGS = 20;

export const jobTitleErrorKey = (id) => `jobPostingTitle-${id}`;

export const jobPostingsFromRecord = (value) => {
  let list = value;
  if (typeof value === 'string') {
    try {
      list = JSON.parse(value);
    } catch (error) {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];

  return list.slice(0, MAX_JOB_POSTINGS).map((item, index) => ({
    id: String(item?.id || `job-${index + 1}`),
    title: String(item?.title || ''),
    description: String(item?.description || ''),
  }));
};

export const catalogImagesFromRecord = (value) => {
  let list = value;
  if (typeof value === 'string') {
    try {
      list = JSON.parse(value);
    } catch (error) {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list.filter((item) => typeof item === 'string' && item).slice(0, MAX_CATALOG_IMAGES);
};

const markInvalid = (errorInputs, name, valid) =>
  valid
    ? errorInputs
    : errorInput({
        errorInputs,
        input: { name, value: false },
      });

const markGuestIdentity = (errorInputs, inputs, { email = false } = {}) => {
  let nextErrors = markInvalid(
    errorInputs,
    'identification',
    isValidIdentification(inputs.identification)
  );
  nextErrors = markInvalid(nextErrors, 'phone', isValidPhone(inputs.phone));
  if (email) {
    nextErrors = markInvalid(nextErrors, 'email', isValidEmail(inputs.email, { required: true }));
  }
  return nextErrors;
};

export const verifyLookupFields = (inputs = {}) => {
  let nextErrors = {};

  ['identification', 'phone'].forEach((field) => {
    nextErrors = errorInput({
      errorInputs: nextErrors,
      input: { name: field, value: inputs[field] },
    });
  });

  return markGuestIdentity(nextErrors, inputs);
};

const BUSINESS_FIELDS = [
  'serviceOffered',
  'businessName',
  'category',
  'comunidadMemeber',
  'businessPhone',
  'leaderPhone',
  'leaderName',
];

const markRequired = (errorInputs, inputs, fields) => {
  let nextErrors = errorInputs;
  fields.forEach((input) => {
    const value =
      input === 'comunidadMemeber' ? yesNoValue(inputs[input]) || inputs[input] : inputs[input];
    nextErrors = errorInput({
      errorInputs: nextErrors,
      input: { name: input, value },
    });
  });
  return nextErrors;
};

const verifyJobPostings = (errorInputs, jobPostings = []) => {
  let nextErrors = errorInputs;
  jobPostingsFromRecord(jobPostings).forEach((posting) => {
    const title = posting.title.trim();
    const description = posting.description.trim();
    if (!title && !description) return;
    nextErrors = errorInput({
      errorInputs: nextErrors,
      input: { name: jobTitleErrorKey(posting.id), value: title },
    });
  });
  return nextErrors;
};

export const verifyCreateNetworkForm = (inputs = {}) => {
  let nextErrors = markRequired({}, inputs, BUSINESS_FIELDS);
  nextErrors = verifyJobPostings(nextErrors, inputs.jobPostings);

  PERSON_FIELDS.forEach((field) => {
    nextErrors = errorInput({
      errorInputs: nextErrors,
      input: { name: field, value: inputs[field] },
    });
  });

  if (!inputs.guest_id) {
    nextErrors = errorInput({
      errorInputs: nextErrors,
      input: { name: 'guest_id', value: false },
    });
  }

  if (inputs.comunidadMemeber === false || inputs.comunidadMemeber === 'No') {
    Swal.fire(
      'Lo sentimos, la publicación en este directorio es un servicio exlusivo para miembros de Comunidad'
    );
    nextErrors = errorInput({
      errorInputs: nextErrors,
      input: { name: 'comunidadMemeber', value: false },
    });
  }

  return markGuestIdentity(nextErrors, inputs, { email: true });
};

export const verifyEditNetworkForm = (inputs = {}) =>
  verifyJobPostings(markRequired({}, inputs, BUSINESS_FIELDS), inputs.jobPostings);

export const hasFormErrors = (errorInputs = {}) => Object.values(errorInputs).includes(true);
